import { createAsyncThunk, createSelector, createSlice } from '@reduxjs/toolkit';

import * as api from './contentCalendarAPI.js';

// Async thunks

export const fetchCalendarItems = createAsyncThunk('contentCalendar/fetchItems', async ({ profileId, from, to, types }, { rejectWithValue }) => {
  try {
    const res = await api.getContentCalendar(profileId, from, to, types);
    return res.data;
  } catch (err) {
    return rejectWithValue(err?.response?.data || err.message);
  }
});

export const fetchUpcoming = createAsyncThunk('contentCalendar/fetchUpcoming', async ({ profileId, limit = 5 }, { rejectWithValue }) => {
  try {
    const res = await api.getUpcoming(profileId, limit);
    return res.data;
  } catch (err) {
    return rejectWithValue(err?.response?.data || err.message);
  }
});

export const fetchStats = createAsyncThunk('contentCalendar/fetchStats', async ({ profileId }, { rejectWithValue }) => {
  try {
    const res = await api.getSchedulerStats(profileId);
    return res.data;
  } catch (err) {
    return rejectWithValue(err?.response?.data || err.message);
  }
});

export const fetchRecurrencePreview = createAsyncThunk('contentCalendar/fetchRecurrencePreview', async (params, { rejectWithValue }) => {
  try {
    const res = await api.getRecurrencePreview(params);
    return res.data;
  } catch (err) {
    return rejectWithValue(err?.response?.data || err.message);
  }
});

export const createTask = createAsyncThunk('contentCalendar/createTask', async ({ profileId, data }, { rejectWithValue }) => {
  try {
    const res = await api.createScheduledTask(profileId, data);
    return res.data;
  } catch (err) {
    return rejectWithValue(err?.response?.data || err.message);
  }
});

export const deleteTask = createAsyncThunk('contentCalendar/deleteTask', async ({ profileId, taskId }, { rejectWithValue }) => {
  try {
    await api.deleteScheduledTask(profileId, taskId);
    return taskId;
  } catch (err) {
    return rejectWithValue(err?.response?.data || err.message);
  }
});

// Slice

const contentCalendarSlice = createSlice({
  name: 'contentCalendar',
  initialState: {
    items: [],
    summary: null,
    warnings: [],
    isLoading: false,
    error: null,

    dateRange: { from: null, to: null },
    typeFilter: [],

    upcoming: [],
    upcomingLoading: false,

    stats: null,
    statsLoading: false,

    recurrencePreview: [],
    recurrencePreviewLoading: false,

    selectedEvent: null,
  },
  reducers: {
    setDateRange: (state, { payload }) => {
      state.dateRange = payload;
    },
    setTypeFilter: (state, { payload }) => {
      state.typeFilter = payload;
    },
    setSelectedEvent: (state, { payload }) => {
      state.selectedEvent = payload;
    },
    clearSelectedEvent: (state) => {
      state.selectedEvent = null;
    },
    clearRecurrencePreview: (state) => {
      state.recurrencePreview = [];
    },
  },
  extraReducers: (builder) => {
    // Calendar items
    builder
      .addCase(fetchCalendarItems.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchCalendarItems.fulfilled, (state, { payload }) => {
        state.isLoading = false;
        state.items = payload.Items || [];
        state.summary = payload.Summary || null;
        state.warnings = payload.Warnings || [];
      })
      .addCase(fetchCalendarItems.rejected, (state, { payload }) => {
        state.isLoading = false;
        state.error = payload;
      });

    // Upcoming
    builder
      .addCase(fetchUpcoming.pending, (state) => {
        state.upcomingLoading = true;
      })
      .addCase(fetchUpcoming.fulfilled, (state, { payload }) => {
        state.upcomingLoading = false;
        state.upcoming = payload || [];
      })
      .addCase(fetchUpcoming.rejected, (state) => {
        state.upcomingLoading = false;
      });

    // Stats
    builder
      .addCase(fetchStats.pending, (state) => {
        state.statsLoading = true;
      })
      .addCase(fetchStats.fulfilled, (state, { payload }) => {
        state.statsLoading = false;
        state.stats = payload;
      })
      .addCase(fetchStats.rejected, (state) => {
        state.statsLoading = false;
      });

    // Recurrence preview
    builder
      .addCase(fetchRecurrencePreview.pending, (state) => {
        state.recurrencePreviewLoading = true;
      })
      .addCase(fetchRecurrencePreview.fulfilled, (state, { payload }) => {
        state.recurrencePreviewLoading = false;
        state.recurrencePreview = payload || [];
      })
      .addCase(fetchRecurrencePreview.rejected, (state) => {
        state.recurrencePreviewLoading = false;
      });
  },
});

export const { setDateRange, setTypeFilter, setSelectedEvent, clearSelectedEvent, clearRecurrencePreview } = contentCalendarSlice.actions;

// Selectors

const selectContentCalendar = (state) => state.contentCalendar;

export const selectCalendarItems = (state) => state.contentCalendar.items;
export const selectCalendarIsLoading = (state) => state.contentCalendar.isLoading;
export const selectCalendarSummary = (state) => state.contentCalendar.summary;
export const selectCalendarWarnings = (state) => state.contentCalendar.warnings;
export const selectCalendarTypeFilter = (state) => state.contentCalendar.typeFilter;
export const selectCalendarDateRange = (state) => state.contentCalendar.dateRange;
export const selectUpcomingItems = (state) => state.contentCalendar.upcoming;
export const selectUpcomingLoading = (state) => state.contentCalendar.upcomingLoading;
export const selectCalendarStats = (state) => state.contentCalendar.stats;
export const selectSelectedEvent = (state) => state.contentCalendar.selectedEvent;
export const selectRecurrencePreview = (state) => state.contentCalendar.recurrencePreview;
export const selectRecurrencePreviewLoading = (state) => state.contentCalendar.recurrencePreviewLoading;

export const selectCalendarEvents = createSelector([selectCalendarItems], (items) =>
  items.map((item) => ({
    id: item.Id,
    title: item.Title || 'Untitled',
    start: new Date(item.ScheduledAt),
    end: new Date(item.ScheduledAt),
    type: item.Type,
    status: item.Status,
    color: item.Color,
    action: item.Action,
    platforms: item.Platforms,
    thumbnail: item.Thumbnail,
    recurrence: item.Recurrence,
  })),
);

export const selectFilteredCalendarEvents = createSelector([selectCalendarEvents, selectCalendarTypeFilter], (events, typeFilter) => {
  if (!typeFilter || typeFilter.length === 0) return events;
  return events.filter((e) => typeFilter.includes(e.type));
});

export default contentCalendarSlice.reducer;
